import { c as _c } from "react/compiler-runtime";
import type { PlayInfo } from '@module/apis/client-side/live/type';
import { Icon } from '@module/components/icons';
import SettingConfig from '@module/config/setting';
import useGetLoginInfoQuery from '@module/helpers/auth/useGetLoginInfoQuery';
import { aspCond } from '@module/helpers/conditions/condition';
import { useAddBlockMutation, useCancelBlockMutation } from '@module/queries/block/blockQueryHooks';
import { usePostCancelManagerMutation, usePostWriteManagerMutation } from '@module/queries/manager/managerQueryHooks';
import useModalStore from '@module/stores/useModalStore';
import useMuteStore from '@module/stores/useMuteStore';
import useSocketStore from '@module/stores/useSocketStore';
import { MODAL_TYPE_PC } from '@module/types/modal/type';
import catchAllErrors from '@module/utils/catchAllErrors';
import masking from '@module/utils/masking';
import isTimestampPast from '@module/utils/time/isTimestampPast';
import type { SendMessageModalType } from '@pc/containers/modal/message/type';
import { getViewerInteractionMenuActions } from './viewerInteractionMenu.policy';
import type { ViewerInteractionMenuAction, ViewerInteractionMenuUser, ViewerInteractionMenuVariant, ViewerInteractionMenuViewer } from './viewerInteractionMenu.types';
interface Props {
  playInfo?: PlayInfo;
  isVisible: boolean;
  user: ViewerInteractionMenuUser;
  viewer: ViewerInteractionMenuViewer;
  variant?: ViewerInteractionMenuVariant;
  onClose: () => void;
  showUserId?: boolean;
}
interface MenuItemDefinition {
  label: string;
  onClick: () => void;
}
function ViewerInteractionMenu(t0) {
  const $ = _c(164);
  const {
    playInfo,
    isVisible,
    user,
    viewer,
    variant: t1,
    onClose,
    showUserId: t2
  } = t0;
  const variant = t1 === undefined ? "default" : t1;
  const showUserId = t2 === undefined ? true : t2;
  const {
    idx: viewerIdx,
    nick: viewerNick,
    id: viewerId
  } = viewer;
  const {
    userInfo,
    isAuthDone
  } = useGetLoginInfoQuery();
  const {
    pushModal,
    popModal,
    clearModals
  } = useModalStore();
  const token = useSocketStore(_temp);
  const deleteUserFromMutedUsers = useMuteStore(_temp2);
  const getMutedUsers = useMuteStore(_temp3);
  let t3;
  if ($[0] !== getMutedUsers || $[1] !== viewerIdx) {
    const mutedTime = getMutedUsers()[String(viewerIdx)];
    t3 = !!mutedTime && !isTimestampPast(mutedTime);
    $[0] = getMutedUsers;
    $[1] = viewerIdx;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const isAlreadyMuted = t3;
  let t4;
  if ($[3] !== clearModals || $[4] !== pushModal) {
    t4 = error => {
      pushModal({
        type: "CONFIRM_MODAL",
        id: "ViewerInteractionMenu",
        options: {
          description: error.message,
          onClose: clearModals
        }
      });
    };
    $[3] = clearModals;
    $[4] = pushModal;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const onError = t4;
  let t5;
  if ($[6] !== onError) {
    t5 = {
      onError: err => catchAllErrors(err, onError)
    };
    $[6] = onError;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const {
    mutate: postWriteManager
  } = usePostWriteManagerMutation(t5);
  let t6;
  if ($[8] !== onError) {
    t6 = {
      onError: err_0 => catchAllErrors(err_0, onError)
    };
    $[8] = onError;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const {
    mutate: postCancelManager
  } = usePostCancelManagerMutation(t6);
  let t7;
  if ($[10] !== onError) {
    t7 = {
      onError: err_1 => catchAllErrors(err_1, onError)
    };
    $[10] = onError;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  const {
    mutate: addBlock
  } = useAddBlockMutation(t7);
  let t8;
  if ($[12] !== deleteUserFromMutedUsers) {
    t8 = (_, variables) => {
      if (variables.type === "mute5") {
        deleteUserFromMutedUsers(String(variables.toUserIdx));
      }
    };
    $[12] = deleteUserFromMutedUsers;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== onError) {
    t9 = err_2 => catchAllErrors(err_2, onError);
    $[14] = onError;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== t8 || $[17] !== t9) {
    t10 = {
      onSuccess: t8,
      onError: t9
    };
    $[16] = t8;
    $[17] = t9;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  const {
    mutate: cancelBlock
  } = useCancelBlockMutation(t10);
  let t11;
  if ($[19] !== pushModal) {
    t11 = t12 => {
      const {
        userId,
        userNick,
        userIdx
      } = t12;
      pushModal({
        type: MODAL_TYPE_PC.SEND_MESSAGE_MODAL,
        id: "ViewerInteractionMenu",
        options: {
          userId,
          userNick,
          userIdx
        }
      });
    };
    $[19] = pushModal;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  const showMessageModal = t11;
  let t12;
  if ($[21] !== pushModal) {
    t12 = t13 => {
      const {
        giftId,
        giftNick
      } = t13;
      pushModal({
        type: MODAL_TYPE_PC.STORE_MODAL,
        id: "ViewerInteractionMenu",
        options: {
          giftId,
          giftNick
        }
      });
    };
    $[21] = pushModal;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  const showStoreModal = t12;
  let t13;
  if ($[23] !== playInfo || $[24] !== pushModal) {
    t13 = t14 => {
      const {
        toUserIdx,
        toUserNickName,
        toUserId
      } = t14;
      if (!playInfo) {
        return;
      }
      pushModal({
        type: MODAL_TYPE_PC.LIVE_PICK_MODAL,
        id: "ViewerInteractionMenu",
        options: {
          toUserIdx,
          toUserNickName,
          toUserId,
          mediaCode: playInfo.media.code
        }
      });
    };
    $[23] = playInfo;
    $[24] = pushModal;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  const showLivePickModal = t13;
  let t14;
  if ($[26] !== playInfo || $[27] !== pushModal) {
    t14 = t15 => {
      const {
        toUserIdx: toUserIdx_0,
        toUserNickName: toUserNickName_0,
        toUserId: toUserId_0
      } = t15;
      if (!playInfo) {
        return;
      }
      pushModal({
        type: MODAL_TYPE_PC.REPORT_MODAL,
        id: "ViewerInteractionMenu",
        options: {
          toUserIdx: toUserIdx_0,
          toUserNickName: toUserNickName_0,
          toUserId: toUserId_0,
          mediaCode: playInfo.media.code,
          type: "user",
          broadcastTitle: playInfo.media.title,
          broadcastId: playInfo.media.userId,
          broadcastNickName: playInfo.media.userNick
        }
      });
    };
    $[26] = playInfo;
    $[27] = pushModal;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  const showViewerReportModal = t14;
  const isMe = userInfo?.idx === viewerIdx;
  let t15;
  let t16;
  let t17;
  let t18;
  let t19;
  let t20;
  let t21;
  if ($[29] !== addBlock || $[30] !== cancelBlock || $[31] !== isAlreadyMuted || $[32] !== isAuthDone || $[33] !== isMe || $[34] !== isVisible || $[35] !== onClose || $[36] !== playInfo || $[37] !== popModal || $[38] !== postCancelManager || $[39] !== postWriteManager || $[40] !== pushModal || $[41] !== showLivePickModal || $[42] !== showMessageModal || $[43] !== showStoreModal || $[44] !== showUserId || $[45] !== showViewerReportModal || $[46] !== token || $[47] !== user || $[48] !== userInfo?.purchaseUser || $[49] !== variant || $[50] !== viewer || $[51] !== viewerId || $[52] !== viewerIdx || $[53] !== viewerNick) {
    t21 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const isAsp = aspCond.if("asp");
      const isAspAndPostAllowed = isAsp && SettingConfig?.aspUtils?.aspInfo?.postYN === "Y" && userInfo?.purchaseUser === "Y";
      const isPostAllowed = !isAsp || isAspAndPostAllowed;
      const menuActions = getViewerInteractionMenuActions({
        user,
        viewer,
        variant,
        context: {
          hasPlayInfo: !!playInfo,
          isAlreadyMuted,
          isAsp,
          isMe,
          isPostAllowed,
          isVisible
        }
      });
      let t22;
      if ($[61] !== onClose || $[62] !== playInfo || $[63] !== postWriteManager || $[64] !== viewerIdx) {
        t22 = {
          label: "\uB9E4\uB2C8\uC800\uC784\uBA85",
          onClick: () => {
            if (!playInfo) {
              return;
            }
            postWriteManager({
              fromUserIdx: playInfo.media.userIdx,
              toUserIdx: viewerIdx
            });
            onClose();
          }
        };
        $[61] = onClose;
        $[62] = playInfo;
        $[63] = postWriteManager;
        $[64] = viewerIdx;
        $[65] = t22;
      } else {
        t22 = $[65];
      }
      let t23;
      if ($[66] !== onClose || $[67] !== playInfo || $[68] !== postCancelManager || $[69] !== viewerIdx) {
        t23 = {
          label: "\uB9E4\uB2C8\uC800\uD574\uC784",
          onClick: () => {
            if (!playInfo) {
              return;
            }
            postCancelManager({
              fromUserIdx: playInfo.media.userIdx,
              toUserIdx: viewerIdx
            });
            onClose();
          }
        };
        $[66] = onClose;
        $[67] = playInfo;
        $[68] = postCancelManager;
        $[69] = viewerIdx;
        $[70] = t23;
      } else {
        t23 = $[70];
      }
      let t24;
      if ($[71] !== addBlock || $[72] !== onClose || $[73] !== playInfo || $[74] !== token || $[75] !== viewerIdx) {
        t24 = {
          label: "\uCC44\uD305\uAE08\uC9C0",
          onClick: () => {
            if (!token || !playInfo) {
              return;
            }
            addBlock({
              token,
              type: "mute5",
              expDay: "",
              mediaCode: playInfo.media.code,
              toUserIdx: viewerIdx
            });
            onClose();
          }
        };
        $[71] = addBlock;
        $[72] = onClose;
        $[73] = playInfo;
        $[74] = token;
        $[75] = viewerIdx;
        $[76] = t24;
      } else {
        t24 = $[76];
      }
      let t25;
      if ($[77] !== cancelBlock || $[78] !== onClose || $[79] !== playInfo || $[80] !== token || $[81] !== viewerIdx) {
        t25 = {
          label: "\uCC44\uD305\uAE08\uC9C0\uD574\uC81C",
          onClick: () => {
            if (!token || !playInfo?.media.code) {
              return;
            }
            cancelBlock({
              token,
              type: "mute5",
              mediaCode: playInfo.media.code,
              toUserIdx: viewerIdx
            });
            onClose();
          }
        };
        $[77] = cancelBlock;
        $[78] = onClose;
        $[79] = playInfo;
        $[80] = token;
        $[81] = viewerIdx;
        $[82] = t25;
      } else {
        t25 = $[82];
      }
      let t26;
      if ($[83] !== addBlock || $[84] !== onClose || $[85] !== playInfo || $[86] !== token || $[87] !== viewerIdx) {
        t26 = {
          label: "\uC601\uAD6C \uAC15\uD1F4",
          onClick: () => {
            if (!token || !playInfo?.media.code) {
              return;
            }
            addBlock({
              token,
              type: "ban",
              expDay: 0,
              mediaCode: playInfo.media.code,
              toUserIdx: viewerIdx
            });
            onClose();
          }
        };
        $[83] = addBlock;
        $[84] = onClose;
        $[85] = playInfo;
        $[86] = token;
        $[87] = viewerIdx;
        $[88] = t26;
      } else {
        t26 = $[88];
      }
      let t27;
      if ($[89] !== addBlock || $[90] !== onClose || $[91] !== playInfo || $[92] !== token || $[93] !== viewerIdx) {
        t27 = {
          label: "1\uC77C \uAC15\uD1F4",
          onClick: () => {
            if (!token || !playInfo?.media.code) {
              return;
            }
            addBlock({
              token,
              type: "ban",
              expDay: 1,
              mediaCode: playInfo.media.code,
              toUserIdx: viewerIdx
            });
            onClose();
          }
        };
        $[89] = addBlock;
        $[90] = onClose;
        $[91] = playInfo;
        $[92] = token;
        $[93] = viewerIdx;
        $[94] = t27;
      } else {
        t27 = $[94];
      }
      let t28;
      if ($[95] !== onClose || $[96] !== showStoreModal || $[97] !== viewerId || $[98] !== viewerNick) {
        t28 = {
          label: "\uC120\uBB3C\uD558\uAE30 - \uC0C1\uC810",
          onClick: () => {
            showStoreModal({
              giftId: viewerId,
              giftNick: viewerNick
            });
            onClose();
          }
        };
        $[95] = onClose;
        $[96] = showStoreModal;
        $[97] = viewerId;
        $[98] = viewerNick;
        $[99] = t28;
      } else {
        t28 = $[99];
      }
      let t29;
      if ($[100] !== isAuthDone || $[101] !== onClose || $[102] !== popModal || $[103] !== pushModal || $[104] !== viewerId || $[105] !== viewerIdx || $[106] !== viewerNick) {
        t29 = {
          label: "\uC120\uBB3C\uD558\uAE30 - \uBCF4\uC720\uC544\uC774\uD15C",
          onClick: () => {
            if (!isAuthDone) {
              pushModal({
                type: MODAL_TYPE_PC.CONFIRM_CANCEL_MODAL,
                id: "ViewerInteractionMenu",
                options: {
                  description: "\uBCF8\uC778\uC778\uC99D\uC774 \uD544\uC694\uD569\uB2C8\uB2E4.\n\uBCF8\uC778\uC778\uC99D \uD6C4 \uC774\uC6A9\uD574 \uC8FC\uC138\uC694",
                  onClick: () => {
                    popModal();
                    pushModal({
                      type: MODAL_TYPE_PC.IDENTITY_VERIFICATION_MODAL,
                      id: "ViewerInteractionMenu"
                    });
                  }
                }
              });
              return;
            }
            pushModal({
              type: MODAL_TYPE_PC.GIFTABLE_ITEM_MODAL,
              id: "ViewerInteractionMenu",
              options: {
                targetUser: {
                  idx: viewerIdx,
                  id: viewerId,
                  nick: viewerNick
                }
              }
            });
            onClose();
          }
        };
        $[100] = isAuthDone;
        $[101] = onClose;
        $[102] = popModal;
        $[103] = pushModal;
        $[104] = viewerId;
        $[105] = viewerIdx;
        $[106] = viewerNick;
        $[107] = t29;
      } else {
        t29 = $[107];
      }
      let t30;
      if ($[108] !== isAuthDone || $[109] !== onClose || $[110] !== pushModal || $[111] !== showMessageModal || $[112] !== viewerId || $[113] !== viewerIdx || $[114] !== viewerNick) {
        t30 = {
          label: "\uCABD\uC9C0\uC4F0\uAE30",
          onClick: () => {
            if (!isAuthDone) {
              pushModal({
                type: MODAL_TYPE_PC.IDENTITY_VERIFICATION_MODAL,
                id: "ViewerInteractionMenu"
              });
              return;
            }
            showMessageModal({
              userId: viewerId,
              userIdx: viewerIdx,
              userNick: viewerNick
            });
            onClose();
          }
        };
        $[108] = isAuthDone;
        $[109] = onClose;
        $[110] = pushModal;
        $[111] = showMessageModal;
        $[112] = viewerId;
        $[113] = viewerIdx;
        $[114] = viewerNick;
        $[115] = t30;
      } else {
        t30 = $[115];
      }
      let t31;
      if ($[116] !== onClose || $[117] !== showLivePickModal || $[118] !== viewerId || $[119] !== viewerIdx || $[120] !== viewerNick) {
        t31 = {
          label: "PICK",
          onClick: () => {
            showLivePickModal({
              toUserIdx: viewerIdx,
              toUserNickName: viewerNick,
              toUserId: viewerId
            });
            onClose();
          }
        };
        $[116] = onClose;
        $[117] = showLivePickModal;
        $[118] = viewerId;
        $[119] = viewerIdx;
        $[120] = viewerNick;
        $[121] = t31;
      } else {
        t31 = $[121];
      }
      let t32;
      if ($[122] !== onClose || $[123] !== showViewerReportModal || $[124] !== viewerId || $[125] !== viewerIdx || $[126] !== viewerNick) {
        t32 = {
          label: "\uC2E0\uACE0\uD558\uAE30",
          onClick: () => {
            showViewerReportModal({
              toUserIdx: viewerIdx,
              toUserNickName: viewerNick,
              toUserId: viewerId
            });
            onClose();
          }
        };
        $[122] = onClose;
        $[123] = showViewerReportModal;
        $[124] = viewerId;
        $[125] = viewerIdx;
        $[126] = viewerNick;
        $[127] = t32;
      } else {
        t32 = $[127];
      }
      let t33;
      if ($[128] !== t22 || $[129] !== t23 || $[130] !== t24 || $[131] !== t25 || $[132] !== t26 || $[133] !== t27 || $[134] !== t28 || $[135] !== t29 || $[136] !== t30 || $[137] !== t31 || $[138] !== t32) {
        t33 = {
          assignManager: t22,
          dismissManager: t23,
          muteChat: t24,
          unmuteChat: t25,
          permanentBan: t26,
          temporaryBan: t27,
          sendGiftStore: t28,
          sendGiftOwnedItem: t29,
          sendPost: t30,
          livePick: t31,
          reportViewer: t32
        };
        $[128] = t22;
        $[129] = t23;
        $[130] = t24;
        $[131] = t25;
        $[132] = t26;
        $[133] = t27;
        $[134] = t28;
        $[135] = t29;
        $[136] = t30;
        $[137] = t31;
        $[138] = t32;
        $[139] = t33;
      } else {
        t33 = $[139];
      }
      const menuItemDefinitions = t33;
      if (!menuActions.length) {
        t21 = null;
        break bb0;
      }
      if ($[140] === Symbol.for("react.memo_cache_sentinel")) {
        t17 = {
          boxShadow: "0 0 20px rgba(0, 0, 0, .1)"
        };
        $[140] = t17;
      } else {
        t17 = $[140];
      }
      t18 = "absolute top-[33px] left-0 z-30 w-full rounded-[15px] bg-colorBg02 text-baseFg";
      t19 = _temp4;
      let t34;
      if ($[141] !== showUserId || $[142] !== viewerId) {
        t34 = showUserId && `(${masking(viewerId)})`;
        $[141] = showUserId;
        $[142] = viewerId;
        $[143] = t34;
      } else {
        t34 = $[143];
      }
      let t35;
      if ($[144] !== t34 || $[145] !== viewerNick) {
        t35 = <div className="truncate font-semibold text-[14px]">{viewerNick}{t34}</div>;
        $[144] = t34;
        $[145] = viewerNick;
        $[146] = t35;
      } else {
        t35 = $[146];
      }
      let t36;
      if ($[147] === Symbol.for("react.memo_cache_sentinel")) {
        t36 = <Icon name="CloseBd" size={16} />;
        $[147] = t36;
      } else {
        t36 = $[147];
      }
      let t37;
      if ($[148] !== onClose) {
        t37 = <button type="button" onClick={onClose} className="px-2">{t36}</button>;
        $[148] = onClose;
        $[149] = t37;
      } else {
        t37 = $[149];
      }
      if ($[150] !== t35 || $[151] !== t37) {
        t20 = <div className="flex h-[40px] items-center justify-between rounded-t-xl bg-primaryBg py-1 pr-2 pl-4 text-colorFg12 ">{t35}{t37}</div>;
        $[150] = t35;
        $[151] = t37;
        $[152] = t20;
      } else {
        t20 = $[152];
      }
      t15 = "flex w-full flex-col p-2 text-sm [&>*]:w-full";
      let t38;
      if ($[153] !== menuItemDefinitions) {
        t38 = action => <div key={action} className="rounded-md hover:bg-colorBg09"><button type="button" className="w-full px-2 py-3 text-left" onClick={menuItemDefinitions[action].onClick}>{menuItemDefinitions[action].label}</button></div>;
        $[153] = menuItemDefinitions;
        $[154] = t38;
      } else {
        t38 = $[154];
      }
      t16 = menuActions.map(t38);
    }
    $[29] = addBlock;
    $[30] = cancelBlock;
    $[31] = isAlreadyMuted;
    $[32] = isAuthDone;
    $[33] = isMe;
    $[34] = isVisible;
    $[35] = onClose;
    $[36] = playInfo;
    $[37] = popModal;
    $[38] = postCancelManager;
    $[39] = postWriteManager;
    $[40] = pushModal;
    $[41] = showLivePickModal;
    $[42] = showMessageModal;
    $[43] = showStoreModal;
    $[44] = showUserId;
    $[45] = showViewerReportModal;
    $[46] = token;
    $[47] = user;
    $[48] = userInfo?.purchaseUser;
    $[49] = variant;
    $[50] = viewer;
    $[51] = viewerId;
    $[52] = viewerIdx;
    $[53] = viewerNick;
    $[54] = t15;
    $[55] = t16;
    $[56] = t17;
    $[57] = t18;
    $[58] = t19;
    $[59] = t20;
    $[60] = t21;
  } else {
    t15 = $[54];
    t16 = $[55];
    t17 = $[56];
    t18 = $[57];
    t19 = $[58];
    t20 = $[59];
    t21 = $[60];
  }
  if (t21 !== Symbol.for("react.early_return_sentinel")) {
    return t21;
  }
  let t22;
  if ($[155] !== t15 || $[156] !== t16) {
    t22 = <div className={t15}>{t16}</div>;
    $[155] = t15;
    $[156] = t16;
    $[157] = t22;
  } else {
    t22 = $[157];
  }
  let t23;
  if ($[158] !== t17 || $[159] !== t18 || $[160] !== t19 || $[161] !== t20 || $[162] !== t22) {
    t23 = <div style={t17} className={t18} onClick={t19}>{t20}{t22}</div>;
    $[158] = t17;
    $[159] = t18;
    $[160] = t19;
    $[161] = t20;
    $[162] = t22;
    $[163] = t23;
  } else {
    t23 = $[163];
  }
  return t23;
}
function _temp4(e) {
  return e.stopPropagation();
}
function _temp3(state_1) {
  return state_1.getMutedUsers;
}
function _temp2(state_0) {
  return state_0.deleteUserFromMutedUsers;
}
function _temp(state) {
  return state.token;
}
export default ViewerInteractionMenu;